import type { ReactNode } from "react";
import { PhoneIcon } from "@heroicons/react/24/outline";

import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import NoticeBanner from "components/NoticeBanner";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import {
  leadFlowEvents,
  type CallScreen,
  type ConfirmSurface,
} from "pages/StartLead/events";
import {
  TRY_ANOTHER_CHANNEL_LABEL,
  accountChannelCopy,
} from "pages/StartLead/signIn";

import { NOT_FOUND_COPY, type ProofOutcome } from "./reentry";

export const TAP_LINK_CLASS = `${LINK_BUTTON_CLASS} min-h-[44px]`;

export function ReentryFrame({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto flex w-full max-w-[560px] flex-1 flex-col items-center justify-center px-4 pb-16 pt-16 2xs:px-5">
      <div className="flex w-full flex-col items-center gap-5 rounded-2xl border border-oat-200 bg-white p-5 text-center shadow-sm 2xs:p-8 sm:p-10">
        {children}
      </div>
    </main>
  );
}

/** The call button of a verify, re-entry or recovery screen; `track` names
 * the screen it sits on, so a tap is reported. */
export function CallButton({
  line,
  track,
}: {
  line: { href: string; label: string };
  track: { surface: ConfirmSurface; screen: CallScreen };
}) {
  const trackEvent = useTrackLeadFlowEvent();
  return (
    <a
      href={line.href}
      onClick={() => trackEvent(leadFlowEvents.callClicked(track))}
      className="inline-flex items-center gap-2 rounded-lg border-2 border-soil bg-transparent px-5 py-3 text-lg font-bold text-soil transition-all hover:bg-soil hover:text-ivory"
    >
      <PhoneIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      Call {line.label}
    </a>
  );
}

export function ProofOutcomeView({
  outcome,
  onRetry,
}: {
  outcome: ProofOutcome;
  onRetry: () => void;
}) {
  return (
    <div className="flex w-full flex-col gap-4 text-left">
      <NoticeBanner tone="error">
        {outcome.kind === "not_found" ? NOT_FOUND_COPY : accountChannelCopy()}
      </NoticeBanner>
      <div>
        <button type="button" className={TAP_LINK_CLASS} onClick={onRetry}>
          {TRY_ANOTHER_CHANNEL_LABEL}
        </button>
      </div>
    </div>
  );
}
